'use client'

import { useAuthState } from '@/app/providers/auth'
import { useLanguage } from '@/components/Link'
import { useCreateOrUpdateMyFavouriteMutation, UserFavouriteType } from '@/generated/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useNotifications } from '@/store/notificationStore'
import { populateFieldsWithTranslations } from '@caterdesk/utils--translations'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { Box } from '@mui/material'

import { sortBy, truncate } from 'lodash'
import React, { useCallback, useMemo, useState } from 'react'

import { roundRating } from '../../../common/utils/rating'
import type { VendorSearchState } from '../../states/useVendorSearchState'
import { getDeliverySummaryFromVendor } from '../delivery-summary/getDeliverySummary'

type Vendor = VendorSearchState['vendors'][number]

type FavouriteButtonProps = {
  isFavourite: boolean
  onToggle: (nextValue: boolean) => void
}

const FavouriteButton: React.FC<FavouriteButtonProps> = ({ isFavourite, onToggle }) => {
  return (
    <Box
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onToggle(!isFavourite)
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}>
      {isFavourite ? (
        <FavoriteIcon sx={{ color: '#FF2869' }} data-testid="HeartIcon" />
      ) : (
        <FavoriteBorderIcon
          sx={{ color: 'white', ':hover': { color: '#FF2869' } }}
          data-testid="HeartIcon"
        />
      )}
    </Box>
  )
}

type Params = {
  vendor: Vendor
  enableFavourite?: boolean
}

export const useSupplierCard = ({ vendor, enableFavourite = false }: Params) => {
  const language = useLanguage()
  const gmRatingDisplayEnabled = useFeatureFlag(SPLITS.GM_ORDER_RATING_DISPLAY)
  const authState = useAuthState()
  const [updateFavourite] = useCreateOrUpdateMyFavouriteMutation()
  const [isFavourite, setIsFavourite] = useState<boolean>(Boolean(vendor.isMyFavourite))
  const { showSuccess, showError } = useNotifications()

  const vendorTranslationData = useMemo(
    () => populateFieldsWithTranslations(vendor, language),
    [vendor, language],
  )

  const description = useMemo(() => {
    const text = vendorTranslationData?.descriptionShort ?? vendor.descriptionShort ?? ''
    if (!text) return undefined
    return truncate(text, { length: 192, separator: ' ' })
  }, [vendorTranslationData, vendor.descriptionShort])

  const image = useMemo(() => sortBy(vendor.images || [], 'position')[0], [vendor.images])

  const deliverySummary = useMemo(() => getDeliverySummaryFromVendor(vendor), [vendor])

  const account = authState.type === 'authenticated' ? authState.user.account : null
  const privateItem = Boolean(
    account && vendor.accountIdsWithPrivateItemAccess?.includes(Number(account.id)),
  )
  const approvedVendor = Boolean(account && vendor.isApprovedSupplier)

  const toggleFavourite = useCallback(
    async (nextValue: boolean) => {
      setIsFavourite(nextValue)
      const favouriteId = Number.parseInt(vendor.id, 10)
      if (Number.isNaN(favouriteId)) return

      try {
        await updateFavourite({
          variables: {
            input: {
              favouriteId,
              favouriteType: UserFavouriteType.Supplier,
              isFavourite: nextValue,
            },
          },
        })
        showSuccess(
          nextValue
            ? `${vendor.companyName} added to favourites.`
            : `${vendor.companyName} removed from favourites.`,
        )
      } catch (error) {
        showError('Something went wrong while updating favourites.')
        setIsFavourite(!nextValue)
      }
    },
    [showError, showSuccess, updateFavourite, vendor.companyName, vendor.id],
  )

  const favorite =
    enableFavourite && authState.type === 'authenticated' ? (
      <FavouriteButton isFavourite={isFavourite} onToggle={toggleFavourite} />
    ) : undefined

  // GM catering rating (display flag on): use the new source, show only when
  // there is at least one rating, and hide the count on the list tile. Display
  // flag off keeps the legacy rating behaviour untouched.
  const hasGmRating = (vendor.gmTotalRatings ?? 0) > 0
  const rating = gmRatingDisplayEnabled ? roundRating(vendor.gmRating ?? 0) : vendor.rating
  const totalRatings = gmRatingDisplayEnabled ? undefined : vendor.totalRatings
  const showRating = gmRatingDisplayEnabled ? hasGmRating : undefined

  return {
    companyName: vendor.companyName,
    description,
    logo: vendor.logo,
    image,
    rating,
    totalRatings,
    showRating,
    favorite,
    tags: vendor.tags,
    vendor: {
      id: vendor.id,
      currency: vendor.currency,
    },
    deliverySummary,
    privateItem,
    approvedVendor,
  }
}
